import { useEffect, useRef, useState } from 'react'
import { Link, NavLink } from 'react-router'
import type { JSX } from 'react'
import { useAuth } from '@/auth/context'
import { ThemeToggle } from '@/components/ThemeToggle'
import { initials } from '@/lib/format'
import { NotificationBell } from './NotificationBell'

export interface Crumb {
  label: string
  to?: string
}

/**
 * Breadcrumbs carry the organisation, because agency owners and support staff
 * both work across several and "which tenant am I in" must be answerable
 * without looking at the sidebar (S-D3).
 */
export function Breadcrumbs({ crumbs }: { crumbs: Crumb[] }): JSX.Element {
  return (
    <nav className="ar-crumbs" aria-label="Breadcrumb">
      {crumbs.map((crumb, index) => {
        const last = index === crumbs.length - 1
        return (
          <span key={`${crumb.label}-${String(index)}`} style={{ display: 'contents' }}>
            {crumb.to && !last ? (
              <Link to={crumb.to}>{crumb.label}</Link>
            ) : (
              <span aria-current={last ? 'page' : undefined}>{crumb.label}</span>
            )}
            {last ? null : <span aria-hidden="true">/</span>}
          </span>
        )
      })}
    </nav>
  )
}

/**
 * The account menu.
 *
 * Closes on Escape and on a click anywhere else, both of which a menu that only
 * toggles on its own button fails to do — and the second is the one people
 * actually hit, because they click the thing they wanted instead of closing the
 * menu first.
 */
function AccountMenu(): JSX.Element {
  const { user, signOut } = useAuth()
  const [open, setOpen] = useState(false)
  const wrapper = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDocument(event: MouseEvent | KeyboardEvent): void {
      if (event instanceof KeyboardEvent) {
        if (event.key === 'Escape') setOpen(false)
        return
      }
      if (event.target instanceof Node && !wrapper.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDocument)
    document.addEventListener('keydown', onDocument)
    return () => {
      document.removeEventListener('mousedown', onDocument)
      document.removeEventListener('keydown', onDocument)
    }
  }, [open])

  const label = user?.email ?? user?.mobile ?? 'Your account'

  return (
    <div className="app-menu" ref={wrapper}>
      <button
        type="button"
        className="ar-btn ar-btn--quiet ar-btn--sm ar-btn--icon"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account menu for ${label}`}
        onClick={() => {
          setOpen((current) => !current)
        }}
      >
        <span className="ar-avatar ar-avatar--sm" aria-hidden="true">
          {initials(label)}
        </span>
      </button>
      {open ? (
        <div className="app-menu__panel" role="menu">
          <div className="app-menu__who">
            <strong>{label}</strong>
            {user && !user.email_verified && user.email ? (
              <span className="ar-badge ar-badge--warning">Address not confirmed</span>
            ) : null}
          </div>
          <div className="app-menu__items">
            <NavLink
              to="/settings/organisation"
              role="menuitem"
              onClick={() => {
                setOpen(false)
              }}
            >
              Settings
            </NavLink>
            <NavLink
              to="/billing"
              role="menuitem"
              onClick={() => {
                setOpen(false)
              }}
            >
              Billing
            </NavLink>
            <NavLink
              to="/organisations"
              role="menuitem"
              onClick={() => {
                setOpen(false)
              }}
            >
              Switch organisation
            </NavLink>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false)
                void signOut()
              }}
            >
              Sign out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

/**
 * The top bar: a menu button below 1100px, breadcrumbs, theme, account.
 *
 * The menu button is rendered here rather than in a separate mobile header so
 * that there is one top bar with one set of breadcrumbs at every width — two
 * headers means two places for the breadcrumb trail to be wrong.
 */
export function TopBar({
  crumbs,
  onOpenMenu,
}: {
  crumbs: Crumb[]
  onOpenMenu: () => void
}): JSX.Element {
  return (
    <header className="app-top">
      <button
        type="button"
        className="ar-btn ar-btn--quiet ar-btn--sm ar-btn--icon app-menu-button"
        aria-label="Open navigation"
        onClick={onOpenMenu}
      >
        <span aria-hidden="true">☰</span>
      </button>
      <Breadcrumbs crumbs={crumbs} />
      <span className="app-spacer" />
      <NotificationBell />
      <ThemeToggle />
      <AccountMenu />
    </header>
  )
}
